import { useCallback, useMemo } from 'react';
import {
  GRANT_CHECK_TYPE,
  GRANT_STATES,
  useCallSuccess,
  useGrants,
  useRootContext,
} from '@jarvis/react-portal-addons';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { DASHBOARD_ENTITLEMENTS } from '../../../utils/printerStatus';
import useUsersApiCall from '../../../hooks/useUsersApiCall';
import useAccountProgramInfosApiCall from '../../../hooks/useAccountProgramInfosApiCall';
import {
  hasActiveGrants,
  hasEntitlementsForEntitlements,
  hasOfferingsForEntitlements,
} from '../../../utils/entitlementHelpers';

const grantsStates = [GRANT_STATES.ENABLED, GRANT_STATES.PENDING, GRANT_STATES.DISABLED];

const SUPPORTED_ENTITLEMENTS = [
  DASHBOARD_ENTITLEMENTS.instantInk,
  DASHBOARD_ENTITLEMENTS.printPlans,
];

const grantsToCheck = [
  {
    grant: DASHBOARD_ENTITLEMENTS.instantInk,
    states: grantsStates,
  },
  {
    grant: DASHBOARD_ENTITLEMENTS.printPlans,
    states: grantsStates,
  },
];

/**
 * Returns the visibility flags for the Consolidated Dashboard cards.
 *
 * @returns {object} { showInstantInkCard: boolean, showHpAllInPlanCard: boolean, showSustainabilityCard: boolean, showGetHpAppCard: boolean }
 */

export const useCardsVisibility = ({ deviceCache }) => {
  const { stack, shell } = useRootContext();
  const {
    grants, orgSelector, store, authProvider,
  } = shell;

  const flags = useFlags();
  const {
    consolidatedPortalInstantInkCard,
    consolidatedPortalHpAllInCard,
    consolidatedPortalSustainabilityCard,
    consolidatedPortalGetHpAppCard,
    grantsSupport,
  } = flags;

  const { grantsCall } = useGrants({
    grants: grantsToCheck,
    init: grantsSupport,
    checkType: GRANT_CHECK_TYPE.INDIVIDUAL,
    grantsInterface: grants,
  });

  const userInfosCall = useUsersApiCall({
    authProvider,
    stack,
    store,
  });

  const programInfos = useAccountProgramInfosApiCall({
    authProvider,
    stack,
    init: false,
    store,
    version: grantsSupport ? 'v3' : 'v2',
  });

  const onUserInfosSuccess = useCallback(
    data => {
      programInfos.makeApiCall({
        accountId: orgSelector.getOrgTenantId(),
        userId: data?.resourceId,
      });
    },
    [programInfos, orgSelector],
  );

  useCallSuccess({
    call: userInfosCall,
    onSuccess: onUserInfosSuccess,
  });

  // Card visibility calculation with improved logic separation
  const cardVisibilityFlags = useMemo(() => {
    const getInstantInkCardVisibility = () => {
      if (!consolidatedPortalInstantInkCard) {
        return { hasInstantInkOffering: false, hasInstantInkEntitlement: false };
      }

      if (grantsSupport) {
        // First check if instantInk or printPlans grant exists
        const hasGrant = hasActiveGrants(grantsCall?.data, SUPPORTED_ENTITLEMENTS);
        if (hasGrant) {
          return {
            hasInstantInkOffering: false,
            hasInstantInkEntitlement: true,
          };
        }
        // If no grant, fall through to use programInfo V3 API
      }

      const hasOfferings = hasOfferingsForEntitlements(programInfos?.data, SUPPORTED_ENTITLEMENTS);
      const hasEntitlements = hasEntitlementsForEntitlements(programInfos?.data, SUPPORTED_ENTITLEMENTS);

      return {
        hasInstantInkOffering: hasOfferings,
        hasInstantInkEntitlement: hasEntitlements,
      };
    };

    const getHpAllInPlanCardVisibility = () => consolidatedPortalHpAllInCard;

    const getSustainabilityCardVisibility = () => consolidatedPortalSustainabilityCard;

    const getHpAppCardVisibility = () => consolidatedPortalGetHpAppCard && deviceCache?.data?.length === 0;

    // Calculate card visibility
    const showHpAllInPlanCard = getHpAllInPlanCardVisibility();
    const { hasInstantInkOffering, hasInstantInkEntitlement } = getInstantInkCardVisibility();
    const showInstantInkCard = hasInstantInkOffering || hasInstantInkEntitlement;

    // Sustainability card only shows if at least one other card is visible
    const otherCardsVisible = showHpAllInPlanCard || showInstantInkCard;
    const showSustainabilityCard = getSustainabilityCardVisibility() && otherCardsVisible;

    // HP App card shows if no devices and at least one other card is visible
    const anyCardVisible = showHpAllInPlanCard || showInstantInkCard || showSustainabilityCard;
    const showGetHpAppCard = getHpAppCardVisibility() && anyCardVisible;

    return {
      showHpAllInPlanCard,
      showInstantInkCard,
      showSustainabilityCard,
      showGetHpAppCard,
      instantInkCardLinkToOverview: hasInstantInkEntitlement,
    };
  }, [
    // Feature flags
    consolidatedPortalInstantInkCard,
    consolidatedPortalHpAllInCard,
    consolidatedPortalSustainabilityCard,
    consolidatedPortalGetHpAppCard,
    // Grants and API data
    grantsSupport,
    grantsCall?.data,
    programInfos?.data,
    // Device cache
    deviceCache?.data?.length,
  ]);

  return cardVisibilityFlags;
};

export default useCardsVisibility;
